import React, { useState, useImperativeHandle, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
  Tooltip,
  Button,
  CircularProgress,
  Collapse,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  alpha,
} from '@mui/material'
import type { Theme } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import HistoryIcon from '@mui/icons-material/History'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditOffIcon from '@mui/icons-material/EditOff'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import { BrandTheme, BrandPaletteEntry, BrandThemeHistoryEntry } from '../../types/gotypes'
import BrandPaletteEditor from './BrandPaletteEditor'
import { uniqueCopyName } from '../../utils/brandTheme'
import { BrandThemeConflictError, BrandThemeConflict } from '../../services/brandThemesApi'
import { canEditBrandTheme, groupBrandThemes, noThemeDeleteReason, noThemeEditReason } from '../../utils/brandThemeOwnership'
import { NOBODY_KNOWN, type WhoToAsk } from '../../utils/permissionReason'
import DisabledReason from '../system/DisabledReason'
import { describeThemeHistoryEntry, formatHistoryTime } from '../../utils/brandThemeHistory'

const NEW_SENTINEL = '__new__'

export interface BrandThemeListEditorHandle {
  /** Opens the create form. Wired to each caller's own "New Theme" button,
   *  which lives outside this component (admin page: Paper header; dialog:
   *  DialogTitle) so the surrounding chrome stays exactly as it was. */
  startCreate: () => void
}

export interface BrandThemeListEditorProps {
  themes: BrandTheme[]
  loading?: boolean
  onSave: (id: string, name: string, palette: Record<string, BrandPaletteEntry>) => Promise<void>
  onCreate?: (name: string, palette: Record<string, BrandPaletteEntry>) => Promise<void>
  onDelete: (id: string) => Promise<void>
  /** Three-tier ownership (#1266): who is looking decides which rows they
   *  may edit (canEditBrandTheme) and which tier each row lists under. */
  currentUserEmail?: string
  /** Manager / Realm: may promote and demote, and edit company-level rows. */
  canPromote?: boolean
  onPromote?: (id: string) => Promise<void>
  onDemote?: (id: string) => Promise<void>
  /** Who did what and when (#1279). When provided, each row gets a History
   *  action that opens the trail, newest first. */
  onLoadHistory?: (id: string) => Promise<BrandThemeHistoryEntry[]>
  /** When provided, each row gets an "Open" action that hands the theme back
   *  to the caller (dialog only today). */
  onOpenTheme?: (theme: BrandTheme) => void
  description: React.ReactNode
  emptyMessage: React.ReactNode
  /** Ask for confirmation via a dialog before deleting — the standalone
   *  ThemeManagerDialog does this; the admin BrandThemesSection deletes
   *  immediately, unchanged from before this refactor (#1066 item 2). */
  confirmDelete?: boolean
  /** Border colour for a collapsed theme row. The admin page used 'grey.200',
   *  the dialog used 'divider' — preserved via this prop so neither caller's
   *  visuals shift. */
  idleBorderColor?: string
  /** Whether clicking anywhere on a theme row expands it (admin page), vs
   *  requiring the explicit edit icon (dialog, which also offers Open). */
  rowClickToExpand?: boolean
  /** Notified whenever the create form opens/closes, so the caller's own
   *  "New Theme" button (rendered outside this component) can disable itself
   *  while creating, matching the pre-refactor behavior of both callers. */
  onCreatingChange?: (isCreating: boolean) => void
  /** Who to ask about a theme the user cannot change (useWhoToAsk at the
   *  page). Until a page passes it the sentence says "a Manager". */
  whoToAsk?: WhoToAsk
}

/**
 * Shared list-edit body for saved brand themes: create form, per-theme row
 * (swatches, name, colour count, expand/edit, delete), and the delete
 * confirmation dialog. Extracted from BrandThemesSection (admin page) and
 * ThemeManagerDialog (dashboard Settings → Appearance / DataViz), which had
 * re-implemented the same state machine and near-identical JSX (#1066 item 2)
 * — fixes to the list-edit behavior now land once. The two callers differ
 * only in chrome (Paper vs Dialog) and in the feature flags above, which this
 * component takes as props rather than assuming.
 */
const BrandThemeListEditor = React.forwardRef<BrandThemeListEditorHandle, BrandThemeListEditorProps>(({
  themes,
  loading = false,
  onSave,
  onCreate,
  onDelete,
  currentUserEmail = '',
  canPromote = false,
  onPromote,
  onDemote,
  onLoadHistory,
  onOpenTheme,
  description,
  emptyMessage,
  confirmDelete = false,
  idleBorderColor = 'divider',
  rowClickToExpand = false,
  onCreatingChange,
  whoToAsk = NOBODY_KNOWN,
}, ref) => {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editPalette, setEditPalette] = useState<Record<string, BrandPaletteEntry>>({})
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<BrandTheme | null>(null)
  // #1265: a name conflict reported by the server (409) for the form that is
  // open. The only way past it is a different name — no overwrite (Matt:
  // "an accident waiting to happen"; delete is the considered action).
  const [conflict, setConflict] = useState<{ kind: 'create' | 'rename'; detail: BrandThemeConflict } | null>(null)
  // #1279: the History dialog: which theme, and its entries once loaded.
  const [history, setHistory] = useState<{ theme: BrandTheme; entries: BrandThemeHistoryEntry[] | null } | null>(null)
  // Matt (#1266): the list will grow — a search bar, matching name or owner.
  const [search, setSearch] = useState('')

  const handleOpenHistory = async (theme: BrandTheme) => {
    if (!onLoadHistory) return
    setHistory({ theme, entries: null })
    try {
      const entries = await onLoadHistory(theme.id)
      setHistory((h) => (h && h.theme.id === theme.id ? { theme, entries } : h))
    } catch {
      setHistory((h) => (h && h.theme.id === theme.id ? { theme, entries: [] } : h))
    }
  }

  const isCreating = expandedId === NEW_SENTINEL
  const createFormRef = useRef<HTMLDivElement>(null)

  const onCreatingChangeRef = useRef(onCreatingChange)
  onCreatingChangeRef.current = onCreatingChange
  useEffect(() => {
    onCreatingChangeRef.current?.(isCreating)
  }, [isCreating])

  const handleStartCreate = () => {
    setExpandedId(NEW_SENTINEL)
    setEditName('')
    setEditPalette({})
    setConflict(null)
  }

  useImperativeHandle(ref, () => ({ startCreate: handleStartCreate }), [])

  // #1264: Duplicate opens the SAME create form, pre-filled. The palette is
  // copied entry for entry (aliases included) so editing the draft never
  // reaches into the source theme, and the suggested name is one no existing
  // theme holds — the server upserts by name, so a colliding name would
  // silently overwrite the source instead of creating a copy.
  const handleStartDuplicate = (theme: BrandTheme) => {
    setExpandedId(NEW_SENTINEL)
    setEditName(uniqueCopyName(theme.name, themes.map((t) => t.name)))
    setEditPalette(Object.fromEntries(
      Object.entries(theme.palette).map(([brand, entry]) => [
        brand,
        { ...entry, ...(entry.aliases ? { aliases: [...entry.aliases] } : {}) },
      ])
    ))
    setConflict(null)
    // The form sits above the list; the row clicked may be far below it.
    createFormRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }

  const canEdit = (theme: BrandTheme) => canEditBrandTheme(theme, currentUserEmail, canPromote)

  const handleExpand = (theme: BrandTheme) => {
    if (!canEdit(theme)) return
    if (expandedId === theme.id) {
      setExpandedId(null)
      return
    }
    setExpandedId(theme.id)
    setEditName(theme.name)
    setEditPalette(theme.palette)
    setConflict(null)
  }

  // The server decides what clashes (it compares the way the unique index
  // does and knows the blast radius); the browser only relays its answer.
  const handleSave = async () => {
    const trimmedName = editName.trim()
    if (!trimmedName || !expandedId || actionLoading) return
    const id = expandedId
    setActionLoading(id)
    try {
      if (isCreating) {
        if (onCreate) await onCreate(trimmedName, editPalette)
      } else {
        await onSave(id, trimmedName, editPalette)
      }
      setExpandedId(null)
      setConflict(null)
    } catch (err) {
      if (err instanceof BrandThemeConflictError) {
        setConflict({ kind: isCreating ? 'create' : 'rename', detail: err.conflict })
        return
      }
      throw err
    } finally {
      setActionLoading(null)
    }
  }

  const conflictAlert = (kind: 'create' | 'rename') => {
    if (!conflict || conflict.kind !== kind) return null
    const { theme_name: name } = conflict.detail
    return (
      <Alert severity="warning">
        {kind === 'create'
          ? `A theme named "${name}" already exists. Choose a different name.`
          : `Another theme is already named "${name}". Choose a different name.`}
      </Alert>
    )
  }

  const deleteNow = async (id: string) => {
    setActionLoading(id)
    try {
      await onDelete(id)
      if (expandedId === id) setExpandedId(null)
    } finally {
      setActionLoading(null)
    }
  }

  const handleRequestDelete = (theme: BrandTheme) => {
    if (confirmDelete) {
      setDeleteTarget(theme)
    } else {
      void deleteNow(theme.id)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    const id = deleteTarget.id
    setDeleteTarget(null)
    await deleteNow(id)
  }

  // #1266: three tiers, the DataViz model. Edit and delete follow the rights
  // above and are disabled-and-marked (#1262 treatment) rather than hidden;
  // Duplicate stays live on every row — that is how you take a colleague's
  // theme; the star promotes/demotes for Managers and Realm users.
  const needle = search.trim().toLowerCase()
  const visibleThemes = needle
    ? themes.filter((t) => t.name.toLowerCase().includes(needle) || (t.user || '').toLowerCase().includes(needle))
    : themes
  const tiers = groupBrandThemes(visibleThemes, currentUserEmail)
  // Same signposting as the DataViz homepage (Matt, #1266): a 4px left-edge
  // marker in a standard system colour plus a faint tint — company =
  // primary, mine = info, colleagues = secondary — so scope reads at a glance.
  const tierSections = [
    { key: 'company', title: 'Company-level themes', items: tiers.company, colour: 'primary' as const, tint: 0.04, empty: 'None yet. Managers and Realm users can promote a theme with the star.' },
    { key: 'mine', title: 'My themes', items: tiers.mine, colour: 'info' as const, tint: 0.05, empty: 'None yet. Create one, or duplicate a colleague\'s.' },
    { key: 'colleagues', title: "My colleagues' themes", items: tiers.colleagues, colour: 'secondary' as const, tint: 0.05, empty: 'None.' },
  ]

  const renderRow = (theme: BrandTheme) => {
    const isExpanded = expandedId === theme.id
    const editable = canEdit(theme)
    return (
        <Box
          key={theme.id}
          sx={{
            borderRadius: 1,
            border: '1px solid',
            borderColor: isExpanded ? 'info.main' : idleBorderColor,
            overflow: 'hidden',
          }}
        >
          {/* Theme row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              ...(rowClickToExpand && editable ? { cursor: 'pointer', '&:hover': { backgroundColor: 'grey.50' } } : {}),
            }}
            onClick={rowClickToExpand && editable ? () => handleExpand(theme) : undefined}
          >
            {/* Color swatches */}
            <Box sx={{ display: 'flex', gap: 0.25, mr: 1 }}>
              {Object.values(theme.palette).slice(0, 6).map((entry, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: entry.color,
                  }}
                />
              ))}
              {Object.keys(theme.palette).length > 6 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  +{Object.keys(theme.palette).length - 6}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                {theme.name}
              </Typography>
              {(theme.user || theme.modified_by) && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {theme.user}
                  {theme.modified_by && ` · changed by ${theme.modified_by}, ${formatHistoryTime(theme.updated_at)}`}
                </Typography>
              )}
            </Box>

            <Chip
              label={`${Object.keys(theme.palette).length} colours`}
              size="small"
              variant="outlined"
            />

            {onOpenTheme && (
              <Tooltip title="Open this theme in the editor">
                <Button
                  size="small"
                  color="info"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  onClick={(e) => { e.stopPropagation(); onOpenTheme(theme) }}
                >
                  Open
                </Button>
              </Tooltip>
            )}

            {onCreate && (
              <Tooltip title="Duplicate theme">
                <IconButton
                  size="small"
                  aria-label={`Duplicate ${theme.name}`}
                  onClick={(e) => { e.stopPropagation(); handleStartDuplicate(theme) }}
                  sx={{ '&:hover': { color: 'info.main' } }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {onLoadHistory && (
              <Tooltip title="History: who changed this theme, and when">
                <IconButton
                  size="small"
                  aria-label={`History ${theme.name}`}
                  onClick={(e) => { e.stopPropagation(); void handleOpenHistory(theme) }}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {canPromote && onPromote && onDemote && (
              <Tooltip title={theme.promoted_at ? 'Demote from company-level' : 'Promote to company-level'}>
                <IconButton
                  size="small"
                  aria-label={theme.promoted_at ? `Demote ${theme.name}` : `Promote ${theme.name}`}
                  onClick={(e) => { e.stopPropagation(); void (theme.promoted_at ? onDemote(theme.id) : onPromote(theme.id)) }}
                >
                  {theme.promoted_at ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}

            {!rowClickToExpand && (editable ? (
              <Tooltip title={isExpanded ? 'Collapse' : 'Edit theme'}>
                <IconButton
                  size="small"
                  aria-label={isExpanded ? `Collapse ${theme.name}` : `Edit ${theme.name}`}
                  onClick={() => handleExpand(theme)}
                  sx={{ '&:hover': { color: 'info.main' } }}
                >
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            ) : (
              <DisabledReason reason={noThemeEditReason(whoToAsk, theme)}>
                <IconButton size="small" aria-label={`Edit ${theme.name}`}>
                  <EditOffIcon fontSize="small" color="disabled" />
                </IconButton>
              </DisabledReason>
            ))}

            {editable ? (
              <Tooltip title="Delete theme">
                <IconButton
                  size="small"
                  aria-label={`Delete ${theme.name}`}
                  onClick={(e) => { e.stopPropagation(); handleRequestDelete(theme) }}
                  disabled={actionLoading === theme.id}
                  sx={{ '&:hover': { color: 'error.main' } }}
                >
                  {actionLoading === theme.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            ) : (
              <DisabledReason reason={noThemeDeleteReason(whoToAsk, theme)}>
                <IconButton size="small" aria-label={`Delete ${theme.name}`}>
                  <DeleteIcon fontSize="small" color="disabled" />
                </IconButton>
              </DisabledReason>
            )}

            {rowClickToExpand && editable && (isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
          </Box>

          {/* Expanded editor */}
          <Collapse in={isExpanded}>
            <Box sx={{ p: 2, pt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                size="small"
                label="Theme name"
                value={editName}
                onChange={(e) => { setEditName(e.target.value); setConflict(null) }}
                fullWidth
              />
              <BrandPaletteEditor palette={editPalette} onChange={setEditPalette} />
              {isExpanded && conflictAlert('rename')}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => { setExpandedId(null); setConflict(null) }}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="info"
                  onClick={() => void handleSave()}
                  disabled={!editName.trim() || actionLoading === theme.id}
                  startIcon={actionLoading === theme.id ? <CircularProgress size={16} /> : undefined}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Box>
    )
  }

  return (
    <>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        {description}
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Create form */}
      <Collapse in={isCreating}>
        <Box
          ref={createFormRef}
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'info.main',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <TextField
            size="small"
            label="Theme name"
            value={editName}
            onChange={(e) => {
              setEditName(e.target.value)
              setConflict(null)
            }}
            fullWidth
            autoFocus
          />
          <BrandPaletteEditor palette={editPalette} onChange={setEditPalette} />
          {conflictAlert('create')}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => {
                setExpandedId(null)
                setConflict(null)
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="info"
              onClick={() => void handleSave()}
              disabled={!editName.trim() || Object.keys(editPalette).length === 0 || actionLoading === NEW_SENTINEL}
              startIcon={actionLoading === NEW_SENTINEL ? <CircularProgress size={16} /> : undefined}
            >
              Create Theme
            </Button>
          </Box>
        </Box>
      </Collapse>

      {!loading && themes.length === 0 && !isCreating && (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      )}

      {!loading && themes.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search themes by name or owner"
            inputProps={{ 'aria-label': 'Search themes' }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          {tierSections.map((section) => (
            <Box
              key={section.key}
              data-testid={`theme-tier-${section.key}`}
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: (theme: Theme) => alpha(theme.palette[section.colour].main, section.tint),
                borderLeft: (theme: Theme) => `4px solid ${theme.palette[section.colour].main}`,
              }}
            >
              <Typography variant="subtitle1" component="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {section.title}{' '}
                <Typography component="span" variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                  ({section.items.length})
                </Typography>
              </Typography>
              {section.items.length === 0 ? (
                <Typography variant="body2" color="text.disabled" sx={{ pl: 0.5 }}>{section.empty}</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {section.items.map((theme) => renderRow(theme))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* #1279: who did what and when, newest first, in words. */}
      <Dialog open={Boolean(history)} onClose={() => setHistory(null)} maxWidth="sm" fullWidth aria-labelledby="theme-history-title">
        <DialogTitle id="theme-history-title">History — {history?.theme.name}</DialogTitle>
        <DialogContent>
          {history?.entries === null && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
          )}
          {history?.entries && history.entries.length === 0 && (
            <DialogContentText>No history recorded yet.</DialogContentText>
          )}
          {history?.entries && history.entries.length > 0 && (
            <List dense disablePadding>
              {history.entries.map((e) => (
                <ListItem key={e.id} disableGutters divider>
                  <ListItemText
                    primary={describeThemeHistoryEntry(e)}
                    secondary={`${e.actor || 'unknown'} · ${formatHistoryTime(e.at)}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistory(null)} color="info">Close</Button>
        </DialogActions>
      </Dialog>

      {confirmDelete && (
        <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete theme?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              "{deleteTarget?.name}" will be deleted for everyone. Dashboards already using its colours keep them until a new theme is applied.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
})

BrandThemeListEditor.displayName = 'BrandThemeListEditor'

export default BrandThemeListEditor
